const PRODUCT_GRID_PAGER_MAX_CONSECUTIVE_FETCHES = 40;

function productGridPagerFetchUrl(rawUrl, sectionId) {
  const url = new URL(rawUrl, window.location.origin);
  url.searchParams.set('section_id', sectionId);
  return `${url.pathname}?${url.searchParams.toString()}`;
}

function observeRevealFor(items) {
  if (typeof IntersectionObserver === 'undefined' || items.length === 0) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting) {
          entry.target.classList.remove('scroll-trigger--offscreen');
          if (entry.target.hasAttribute('data-cascade')) {
            entry.target.setAttribute('style', `--animation-order: ${index};`);
          }
          observer.unobserve(entry.target);
        } else {
          entry.target.classList.add('scroll-trigger--offscreen');
        }
      });
    },
    { rootMargin: '0px 0px -50px 0px' }
  );

  items.forEach((item) => observer.observe(item));
}

class RawPageStream {
  static cache = [];

  constructor(sectionId, nextUrl) {
    this.sectionId = sectionId;
    this.nextUrl = nextUrl || null;
    this.buffer = [];
  }

  get hasMore() {
    return this.buffer.length > 0 || !!this.nextUrl;
  }

  async fetchNextRawPage() {
    if (!this.nextUrl) return;

    const fetchUrl = productGridPagerFetchUrl(this.nextUrl, this.sectionId);
    const cached = RawPageStream.cache.find((entry) => entry.url === fetchUrl);
    let html = cached ? cached.html : null;

    if (!html) {
      const response = await fetch(fetchUrl);
      if (!response.ok) throw new Error(`product-grid-pager: fetch failed (${response.status})`);
      html = await response.text();
      RawPageStream.cache.push({ url: fetchUrl, html });
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const pager = doc.querySelector('product-grid-pager');
    const items = pager ? Array.from(pager.querySelectorAll('#product-grid > li')) : [];

    this.nextUrl = (pager && pager.dataset.nextPageUrl) || null;
    this.buffer.push(...items);
  }

  async take(n) {
    let consecutiveFetches = 0;

    while (this.buffer.length < n && this.nextUrl) {
      consecutiveFetches += 1;
      if (consecutiveFetches > PRODUCT_GRID_PAGER_MAX_CONSECUTIVE_FETCHES) {
        throw new Error('product-grid-pager: too many consecutive fetches, aborting');
      }
      await this.fetchNextRawPage();
    }

    return this.buffer.splice(0, n);
  }
}

class ProductGridPager extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.perPage = parseInt(this.dataset.productsPerPage, 10);
    this.mode = this.dataset.paginationMode === 'infinite' ? 'infinite' : 'numbered';
    this.labels = {
      previous: this.dataset.labelPrevious,
      next: this.dataset.labelNext,
      pageTemplate: this.dataset.labelPageTemplate,
    };

    this.list = this.querySelector('#product-grid');
    this.pageCache = new Map();
    this.loading = false;

    const originalWrapper = this.querySelector('.pagination-wrapper');
    this.paginationAriaLabel = originalWrapper?.querySelector('nav')?.getAttribute('aria-label') || '';
    this.iconCaretHtml = originalWrapper?.querySelector('.icon-caret')?.outerHTML || '';
    if (this.mode === 'numbered') originalWrapper?.classList.add('hidden');

    this.boundPopState = this.onPopState.bind(this);
    window.addEventListener('popstate', this.boundPopState);

    const requestedPage = this.getRequestedPageFromUrl();
    const onReady = () => {
      if (this.mode === 'infinite') {
        this.observeSentinel();
      } else {
        this.renderPagerControls();
      }
    };

    if (requestedPage > 1) {
      // A raw Shopify page's boundaries can drift from virtual (post-filter) page
      // boundaries once an earlier page borrowed items from it to fill a gap, so the
      // server-rendered raw page N can't be trusted when landing here directly
      // (reload, deep link, browser restore). Replay sequentially from page 1 to
      // reconstruct the same virtual page N that clicking through would have produced.
      this.stream = new RawPageStream(this.sectionId, this.getRawPage1Url());
      this.ready = this.replayToPage(requestedPage)
        .catch((error) => {
          console.error(error);
        })
        .then(onReady);
    } else {
      this.stream = new RawPageStream(this.sectionId, this.dataset.nextPageUrl || null);
      this.currentVirtualPage = 1;
      this.pageCache.set(1, Array.from(this.list.children).map((li) => li.outerHTML));
      this.ready = this.topUpCurrentPage().catch((error) => {
        console.error(error);
      }).then(onReady);
    }
  }

  getRequestedPageFromUrl() {
    const urlPage = parseInt(new URLSearchParams(window.location.search).get('page'), 10);
    return urlPage > 1 ? urlPage : 1;
  }

  getRawPage1Url() {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    return `${url.pathname}${url.search}`;
  }

  async replayToPage(targetPage) {
    this.closest('.collection')?.classList.add('loading');
    // The server-rendered raw page N may not match the virtual page N being
    // reconstructed below, so clear it instead of showing it (even dimmed) mid-replay.
    this.list.innerHTML = '';
    try {
      let lastFilledPage = 0;
      for (let page = 1; page <= targetPage; page += 1) {
        const items = await this.stream.take(this.perPage);
        if (items.length === 0) break;
        this.pageCache.set(page, items.map((li) => li.outerHTML));
        lastFilledPage = page;
      }

      this.currentVirtualPage = lastFilledPage || 1;
      this.setListItems(this.htmlToNodes(this.pageCache.get(this.currentVirtualPage) || []));
    } finally {
      this.closest('.collection')?.classList.remove('loading');
    }
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.boundPopState);
    if (this.io) this.io.disconnect();
  }

  async topUpCurrentPage() {
    const missing = this.perPage - this.list.children.length;
    if (missing <= 0) return;

    this.closest('.collection')?.classList.add('loading');
    try {
      const items = await this.stream.take(missing);
      items.forEach((li) => this.list.appendChild(li));
      observeRevealFor(items);

      this.pageCache.set(this.currentVirtualPage, Array.from(this.list.children).map((li) => li.outerHTML));
    } finally {
      this.closest('.collection')?.classList.remove('loading');
    }
  }

  htmlToNodes(htmlStrings) {
    const template = document.createElement('template');
    template.innerHTML = htmlStrings.join('');
    return Array.from(template.content.children);
  }

  setListItems(nodes) {
    this.list.innerHTML = '';
    nodes.forEach((node) => this.list.appendChild(node));
    observeRevealFor(nodes);
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }

  navigateFallback(page) {
    const params = new URLSearchParams(window.location.search);
    if (page > 1) {
      params.set('page', page);
    } else {
      params.delete('page');
    }
    const query = params.toString();
    window.location.href = `${window.location.pathname}${query ? `?${query}` : ''}`;
  }

  updateUrl(page, { pushUrl = true } = {}) {
    if (!pushUrl) return;
    const params = new URLSearchParams(window.location.search);
    if (page > 1) {
      params.set('page', page);
    } else {
      params.delete('page');
    }
    const query = params.toString();
    const url = `${window.location.pathname}${query ? `?${query}` : ''}`;
    history.pushState({ source: 'product-grid-pager', sectionId: this.sectionId, page }, '', url);
  }

  onPopState(event) {
    if (event.state && event.state.source === 'product-grid-pager') {
      if (event.state.sectionId !== this.sectionId) return;
      this.goToPage(event.state.page, { pushUrl: false });
      return;
    }
    if (!event.state && this.mode === 'numbered') {
      this.goToPage(1, { pushUrl: false });
    }
  }

  renderPagerControls() {
    let wrapper = this.querySelector('.pagination-wrapper');
    if (!wrapper) return;
    wrapper.classList.remove('hidden');

    const hasPrevious = this.currentVirtualPage > 1;
    const hasNext = this.pageCache.has(this.currentVirtualPage + 1) || this.stream.hasMore;
    const pageLabel = (page) => (this.labels.pageTemplate ? this.labels.pageTemplate.replace('%d', page) : page);

    const pageButtonHtml = (page) => `
      <li>
        <button
          type="button"
          class="pagination__item link motion-reduce"
          data-page="${page}"
          aria-label="${pageLabel(page)}"
        >${page}</button>
      </li>
    `;

    // Pages already cached are confirmed (navigation is strictly sequential, so the
    // cache always holds an unbroken 1..N range). hasNext being true already means the
    // stream confirmed at least one more item is waiting, so it's safe to also surface
    // that next page's number instead of only a bare arrow.
    const cachedPages = Array.from(this.pageCache.keys()).sort((a, b) => a - b);
    const maxCachedPage = cachedPages.length ? cachedPages[cachedPages.length - 1] : this.currentVirtualPage;

    let pageItemsHtml = '';
    cachedPages.forEach((page) => {
      pageItemsHtml +=
        page === this.currentVirtualPage
          ? `
            <li>
              <span
                class="pagination__item pagination__item--current light"
                aria-current="page"
                aria-label="${pageLabel(page)}"
              >${page}</span>
            </li>
          `
          : pageButtonHtml(page);
    });

    if (hasNext && maxCachedPage === this.currentVirtualPage) {
      pageItemsHtml += pageButtonHtml(maxCachedPage + 1);
    }

    const previousItemHtml = hasPrevious
      ? `
        <li>
          <button
            type="button"
            class="pagination__item pagination__item-arrow link motion-reduce"
            data-direction="previous"
            aria-label="${this.labels.previous || ''}"
          >
            <span class="svg-wrapper">${this.iconCaretHtml}</span>
          </button>
        </li>
      `
      : '';

    const nextItemHtml = hasNext
      ? `
        <li>
          <button
            type="button"
            class="pagination__item pagination__item-arrow link motion-reduce"
            data-direction="next"
            aria-label="${this.labels.next || ''}"
          >
            <span class="svg-wrapper">${this.iconCaretHtml}</span>
          </button>
        </li>
      `
      : '';

    wrapper.innerHTML = `
      <nav class="pagination" role="navigation" aria-label="${this.paginationAriaLabel}">
        <ul class="pagination__list list-unstyled" role="list">
          ${previousItemHtml}
          ${pageItemsHtml}
          ${nextItemHtml}
        </ul>
      </nav>
    `;

    wrapper.querySelectorAll('[data-page]').forEach((button) => {
      button.addEventListener('click', () => this.goToPage(parseInt(button.dataset.page, 10)));
    });
    wrapper.querySelector('[data-direction="previous"]')?.addEventListener('click', () => {
      this.goToPage(this.currentVirtualPage - 1);
    });
    wrapper.querySelector('[data-direction="next"]')?.addEventListener('click', () => {
      this.goToPage(this.currentVirtualPage + 1);
    });
  }

  async goToPage(page, { pushUrl = true } = {}) {
    if (page < 1 || this.loading) return;

    if (this.pageCache.has(page)) {
      this.setListItems(this.htmlToNodes(this.pageCache.get(page)));
      this.currentVirtualPage = page;
      this.updateUrl(page, { pushUrl });
      this.renderPagerControls();
      this.scrollToTop();
      return;
    }

    if (page !== this.currentVirtualPage + 1) {
      this.navigateFallback(page);
      return;
    }

    this.loading = true;
    this.closest('.collection')?.classList.add('loading');
    try {
      const items = await this.stream.take(this.perPage);
      if (items.length === 0) return;

      this.pageCache.set(page, items.map((li) => li.outerHTML));
      this.setListItems(items);
      this.currentVirtualPage = page;
      this.updateUrl(page, { pushUrl });
      this.scrollToTop();
    } finally {
      this.loading = false;
      this.closest('.collection')?.classList.remove('loading');
      this.renderPagerControls();
    }
  }

  observeSentinel() {
    this.sentinel = this.querySelector('.pagination-sentinel');
    if (!this.sentinel) return;

    this.sentinel.querySelector('.pagination-sentinel__fallback-link')?.classList.add('hidden');

    if (!this.stream.hasMore) {
      this.showSentinelEndMessage();
      return;
    }

    this.io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) this.loadMoreForInfinite();
      },
      { rootMargin: '400px 0px 0px 0px' }
    );
    this.io.observe(this.sentinel);
  }

  setSentinelState(state) {
    const spinner = this.sentinel.querySelector('.pagination-sentinel__spinner');
    const loadingMessage = this.sentinel.querySelector('.pagination-sentinel__loading-message');
    const endMessage = this.sentinel.querySelector('.pagination-sentinel__end-message');
    const error = this.sentinel.querySelector('.pagination-sentinel__error');
    spinner?.classList.toggle('hidden', state !== 'loading');
    loadingMessage?.classList.toggle('hidden', state !== 'loading');
    endMessage?.classList.toggle('hidden', state !== 'end');
    error?.classList.toggle('hidden', state !== 'error');
  }

  showSentinelEndMessage() {
    this.setSentinelState('end');
  }

  async loadMoreForInfinite() {
    if (this.loading || !this.stream.hasMore) return;
    this.loading = true;
    this.setSentinelState('loading');

    try {
      const items = await this.stream.take(this.perPage);
      items.forEach((li) => this.list.appendChild(li));
      observeRevealFor(items);
    } catch (error) {
      console.error(error);
      this.io?.disconnect();
      this.setSentinelState('error');
      this.loading = false;
      return;
    }

    this.loading = false;

    if (!this.stream.hasMore) {
      this.io?.disconnect();
      this.showSentinelEndMessage();
    } else {
      this.setSentinelState('none');
    }
  }
}

customElements.define('product-grid-pager', ProductGridPager);
